import { useMemo } from "react"
import { Block, Button, useStyletron } from "@mezo-org/mezo-clay"

import { selectUpdateLoanAmount, useDappSelector } from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import SuccessfulActionModalBody from "#/shared/components/modals/SuccessfulActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import { separateThousandsWithComma } from "#/shared/utils"
import { ComponentStepProps } from "#/ui/Modal"

import { BorrowProgress } from "../components"

type UpdateTransactionProps = ComponentStepProps & {
  isLoading: boolean
  hasError: boolean
}

export default function UpdateTransaction({
  onClickContinue,
  onClickBack,
  onClose,
  isLoading,
  hasError,
}: UpdateTransactionProps) {
  const [css, theme] = useStyletron()

  const updateLoanAmount = useDappSelector(selectUpdateLoanAmount)

  const content = useMemo(() => {
    if (hasError) return <FailedActionModalBody onClickNextStep={onClickBack} />

    if (isLoading)
      return (
        <WaitingForActionModalBody
          title={
            <>
              Wait until we update your loan.
              <br /> This might take some time.
              <br />
              Don&apos;t close this window.
            </>
          }
        />
      )

    return (
      <SuccessfulActionModalBody
        title={`${separateThousandsWithComma(updateLoanAmount, 2)} MUSD`}
        description="Congratulations, your MUSD is on its way!"
        nextStepButtonLabel="View loan"
        onClickContinue={onClickContinue}
        footer={
          <Block
            className={css({
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: theme.sizing.scale500,
            })}
          >
            <Button width="100%" size="large" onClick={onClose}>
              View loan
            </Button>
          </Block>
        }
      />
    )
  }, [
    css,
    hasError,
    isLoading,
    onClickBack,
    onClickContinue,
    onClose,
    theme.sizing.scale500,
    updateLoanAmount,
  ])

  return (
    <>
      {(isLoading || hasError) && <BorrowProgress step={3} stepsCount={4} />}
      <Block
        className={css({
          flexDirection: "column",
          display: "flex",
          gap: theme.sizing.scale300,
          padding: isLoading ? theme.sizing.scale800 : 0,
          paddingTop: 0,
          height: "100%",
        })}
      >
        {content}
      </Block>
    </>
  )
}
